import _ from 'lodash';
import { valueFromASTUntyped } from 'graphql';

// Originals stay untouched. The session map is what queries return and what writes change.
let originals = {};
let responses = {};
let idSequence = 0;

const loadGraphqlOriginals = (loaded) => {
    originals = _.cloneDeep(loaded || {});
    resetGraphqlSession();
};

const resetGraphqlSession = () => {
    responses = _.cloneDeep(originals);
    idSequence = 0;
};

const graphqlResponses = () => responses;

const insertGraphqlItem = (operationName, listPath, item) => {
    const list = _.get(responses[operationName], listPath);
    if (Array.isArray(list)) {
        list.push(item);
    }
};

const removeGraphqlItem = (operationName, listPath, idField, id) => {
    const body = responses[operationName];
    const list = _.get(body, listPath);
    if (!Array.isArray(list)) {
        return;
    }
    _.set(body, listPath, list.filter((entry) => entry?.[idField] !== id));
};

const readArgs = (field, variables) => {
    const args = {};
    (field.arguments || []).forEach((argument) => {
        args[argument.name.value] = valueFromASTUntyped(argument.value, variables || {});
    });
    return args;
};

const applyFieldMap = (target, args, fieldMap) => {
    Object.entries(fieldMap || {}).forEach(([targetPath, sourcePath]) => {
        const value = _.get(args, sourcePath);
        if (value !== undefined) {
            _.set(target, targetPath, value);
        }
    });
};

const listAt = (key, listPath) => {
    if (!responses[key] || typeof responses[key] !== 'object') {
        responses[key] = {};
    }
    if (!Array.isArray(_.get(responses[key], listPath))) {
        _.set(responses[key], listPath, []);
    }
    return _.get(responses[key], listPath);
};

const keysFor = (spec, args) => {
    if (spec.targetFrom) {
        const raw = _.get(args, spec.targetFrom);
        return [`${spec.targetPrefix || ''}${_.kebabCase(String(raw ?? ''))}`];
    }
    if (spec.target) {
        return [spec.target];
    }
    if (spec.targetPrefix) {
        return Object.keys(responses).filter((key) => key.startsWith(spec.targetPrefix));
    }
    return [];
};

const writeDetail = (spec, item) => {
    if (!spec.detailPrefix || !item) {
        return;
    }
    const id = item[spec.idField || 'id'];
    const envelope = {};
    _.set(envelope, spec.detailPath, _.cloneDeep(item));
    responses[`${spec.detailPrefix}${id}`] = envelope;

    const fallback = spec.detailFallback ? responses[spec.detailFallback] : undefined;
    if (fallback && _.get(fallback, spec.detailIdPath) === id) {
        _.set(fallback, spec.detailPath, _.cloneDeep(item));
    }
};

const clearDetail = (spec, item) => {
    if (!spec.detailPrefix || !item) {
        return;
    }
    const id = item[spec.idField || 'id'];
    delete responses[`${spec.detailPrefix}${id}`];
    const fallback = spec.detailFallback ? responses[spec.detailFallback] : undefined;
    if (fallback && _.get(fallback, spec.detailIdPath) === id) {
        _.set(fallback, spec.detailPath, null);
    }
};

const upsertItem = (spec, args) => {
    const idField = spec.idField || 'id';
    const key = keysFor(spec, args)[0];
    if (!key) {
        return {};
    }
    const list = listAt(key, spec.listPath);
    const matchField = spec.matchField;
    const matchValue = matchField ? _.get(args, spec.fieldMap?.[matchField] || matchField) : undefined;
    const index = matchField ? list.findIndex((entry) => entry?.[matchField] === matchValue) : -1;
    if (index >= 0) {
        applyFieldMap(list[index], args, spec.fieldMap);
        writeDetail(spec, list[index]);
        return _.cloneDeep(list[index]);
    }
    const item = {};
    applyFieldMap(item, args, spec.fieldMap);
    if (!item[idField]) {
        item[idField] = `${key}-${Date.now()}-${idSequence}`;
        idSequence += 1;
    }
    list.push(item);
    writeDetail(spec, item);
    return _.cloneDeep(item);
};

const patchItem = (spec, args) => {
    const idField = spec.idField || 'id';
    const id = args[spec.idArg || idField];
    for (const key of keysFor(spec, args)) {
        const list = _.get(responses[key], spec.listPath);
        if (!Array.isArray(list)) {
            continue;
        }
        const index = list.findIndex((entry) => entry?.[idField] === id);
        if (index === -1) {
            continue;
        }
        applyFieldMap(list[index], args, spec.fieldMap);
        writeDetail(spec, list[index]);
        return _.cloneDeep(list[index]);
    }
    const missing = { [idField]: id };
    applyFieldMap(missing, args, spec.fieldMap);
    return missing;
};

const removeItem = (spec, args) => {
    const idField = spec.idField || 'id';
    const id = args[spec.idArg || idField];
    let removed;
    for (const key of keysFor(spec, args)) {
        const list = _.get(responses[key], spec.listPath);
        if (!Array.isArray(list)) {
            continue;
        }
        const index = list.findIndex((entry) => entry?.[idField] === id);
        if (index === -1) {
            continue;
        }
        removed = list[index];
        list.splice(index, 1);
    }
    if (removed) {
        clearDetail(spec, removed);
    }
    if (spec.scalar) {
        return true;
    }
    return _.cloneDeep(removed || { [idField]: id });
};

/**
 * Applies a GraphQL mutation to the session copy of a query payload.
 * Returns undefined when the field has no graphqlMutations entry.
 */
const handleGraphqlMutation = (parsedQuery, variables, mutationSeeders) => {
    const definition = parsedQuery?.definitions?.[0];
    if (definition?.operation !== 'mutation') {
        return undefined;
    }
    const field = definition.selectionSet?.selections?.find((selection) => selection.kind === 'Field');
    const spec = mutationSeeders?.[field?.name?.value];
    if (!field || !spec) {
        return undefined;
    }
    const args = readArgs(field, variables);
    let payload;
    if (spec.mutate === 'upsert' || spec.mutate === 'insert' || spec.mutate === 'create') {
        payload = upsertItem(spec, args);
    } else if (spec.mutate === 'patch' || spec.mutate === 'update') {
        payload = patchItem(spec, args);
    } else if (spec.mutate === 'remove') {
        payload = removeItem(spec, args);
    } else {
        return undefined;
    }
    return { data: { [field.name.value]: payload } };
};

export {
    loadGraphqlOriginals,
    resetGraphqlSession,
    graphqlResponses,
    insertGraphqlItem,
    removeGraphqlItem,
    handleGraphqlMutation
};
